// pages/ProjectValuesPage.tsx
import React from "react";
import styled from "styled-components";

// Importation des images (adaptez les noms selon vos fichiers)
import image1 from "../../assets/motricite.jpg";
import image2 from "../../assets/parentalite.jpg";
import image3 from "../../assets/activite.avif";
import image4 from "../../assets/meeko.png"; // Pour Communication
import image5 from "../../assets/bio.jpg";

const primaryColor = "#15b5b0";
const textColor = "#333";

const FaSeedling = require("react-icons/fa").FaSeedling;
const FaHandsHelping = require("react-icons/fa").FaHandsHelping;
const FaSchool = require("react-icons/fa").FaSchool;
const FaComments = require("react-icons/fa").FaComments;
const FaUtensils = require("react-icons/fa").FaUtensils;

/* Container global de la page, avec animation fade-in */
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0px 20px 25px 20px;
  animation: fadeIn 0.8s ease-in-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

/* Titre principal de la page */
const PageTitle = styled.h1`
  font-family: "Cursive Dumont", cursive;
  color: ${primaryColor};
  text-align: center;
  font-size: 3rem;
  margin-bottom: 40px;
`;

/* Wrapper global de la timeline avec la ligne verticale centrale */
const TimelineWrapper = styled.div`
  position: relative;
  padding: 20px 0;

  /* Ligne verticale centrale */
  &::before {
    content: "";
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${primaryColor};
    transform: translateX(-50%);

    @media (max-width: 768px) {
      display: none; /* ❌ Supprime la ligne en mode mobile */
    }
  }
`;

/* Chaque ligne de la timeline organisée en grid 3 colonnes */
const TimelineRowGrid = styled.div<{ side: "left" | "right" }>`
  display: grid;
  grid-template-columns: 1fr 80px 1fr;
  align-items: stretch;
  margin-bottom: 65px;
  gap: 0;
  height: 300px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column; /* Mode mobile : Texte puis Image */
    align-items: stretch; /* Les éléments vont s'étendre sur toute la largeur */
    text-align: center;
    height: auto;
    gap: 10px;
    margin-bottom: 30px;
  }
`;

/* Conteneur pour le texte (card) ; il s'étire pour être de la même hauteur que le conteneur image */
const TextWrapper = styled.div`
  padding-right: 20px;
  padding-left: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    padding-right: 0px;
    padding-left: 0px;
    order: 1; /* Toujours en premier en mobile */
    width: 100%; /* Prend toute la largeur */
  }
`;
/* Conteneur pour l'image décorative ; doit avoir les mêmes dimensions que TextWrapper */
const ImageWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    margin-top: 20px;
    height: 200px;
    order: 2; /* Toujours en deuxième en mobile */
    width: 100%; /* Prend toute la largeur */
  }
`;

/* L'image qui s'adapte au conteneur */
const LargeImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 8px;
  transition: transform 0.3s ease;
  &:hover {
    transform: scale(1.05);
  }
`;

/* Cellule centrale contenant l'icône */
const CenterCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

/* Conteneur de l'icône */
const IconContainer = styled.div`
  width: 50px;
  height: 50px;
  background: ${primaryColor};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.5rem;
  z-index: 1;

  @media (max-width: 768px) {
    display: none; /* ❌ Cache les icônes sur mobile */
  }
`;

/* Titres et textes à l'intérieur du conteneur texte */
const ItemTitle = styled.h2`
  font-size: 1.8rem;
  color: ${primaryColor};
  margin: 5px 0;
`;

const ItemText = styled.p`
  font-size: 1.1rem;
  color: ${textColor};
  line-height: 1.5;
  margin: 0;
`;

/* Composant pour chaque valeur, avec une prop "side" qui détermine l'ordre des colonnes */
interface ValueItemProps {
  title: string;
  text: string;
  icon: React.ReactNode;
  image: string;
  side: "left" | "right"; // "left": texte à gauche, image à droite; "right": inversé.
}

const ValueItem: React.FC<ValueItemProps> = ({
  title,
  text,
  icon,
  image,
  side,
}) => {
  return (
    <TimelineRowGrid side={side}>
      {side === "left" ? (
        <>
          <TextWrapper>
            <ItemTitle>{title}</ItemTitle>
            <ItemText>{text}</ItemText>
          </TextWrapper>
          <CenterCell>
            <IconContainer>{icon}</IconContainer>
          </CenterCell>
          <ImageWrapper>
            <LargeImage src={image} alt={title} />
          </ImageWrapper>
        </>
      ) : (
        <>
          <ImageWrapper>
            <LargeImage src={image} alt={title} />
          </ImageWrapper>
          <CenterCell>
            <IconContainer>{icon}</IconContainer>
          </CenterCell>
          <TextWrapper>
            <ItemTitle>{title}</ItemTitle>
            <ItemText>{text}</ItemText>
          </TextWrapper>
        </>
      )}
    </TimelineRowGrid>
  );
};

const ProjectValuesPage: React.FC = () => {
  return (
    <Container>
      <PageTitle>Nos valeurs</PageTitle>
      <TimelineWrapper>
        <ValueItem
          side="left"
          title="Bienveillance et motricité libre"
          text="Nous plaçons la bienveillance au cœur de notre accompagnement, en respectant le rythme et les besoins de chaque enfant. La motricité libre leur permet d’explorer et de développer leurs capacités en toute sécurité."
          icon={<FaSeedling />}
          image={image1}
        />
        <ValueItem
          side="right"
          title="Co-éducation et parentalité"
          text="Nous travaillons main dans la main avec les familles, convaincues que la co-éducation renforce la confiance et favorise l’épanouissement de l’enfant. Nous proposons des ateliers parents / enfants pour renforcer ces liens "
          icon={<FaHandsHelping />}
          image={image2}
        />
        <ValueItem
          side="left"
          title="Partenariats locaux"
          text="Grâce à l’application Meeko, vous pouvez suivre chaque jour le déroulé de la journée, recevoir des photos et informations pratiques sur la vie de votre enfant à la crèche. Aussi, cette application permet de pouvoir communiquer avec l’équipe et réaliser des demandes d’accueil occassionnels."
          icon={<FaSchool />}
          image={image3}
        />
        <ValueItem
          side="right"
          title="Communication"
          text="Nous utilisons l’application Meeko pour suivre le déroulé de la journée, partager photos, documents et messages, et répondre aux demandes d’accueil."
          icon={<FaComments />}
          image={image4}
        />
        <ValueItem
          side="left"
          title="Repas bio et soins"
          text="Les repas sont fournis par notre partenaire Potion des Lutins, spécialiste de l’alimentation saine et adaptée aux besoins des tout-petits. Aussi, pour le respect de la peau des enfants, nous fournissons et utilisons des produits d’hygiène sains et naturels."
          icon={<FaUtensils />}
          image={image5}
        />
      </TimelineWrapper>
    </Container>
  );
};

export default ProjectValuesPage;
